import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { API_BASE_URL } from "@/lib/config";
import { CreditCard, Loader2 } from "lucide-react";

/**
 * Card verification step (Stripe SetupIntent). Saves a card without
 * charging it; required before the first document send depending on
 * the signup settings managed in the admin panel.
 */
export default function VerifyPayment() {
  const [, setLocation] = useLocation();
  const { language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stripeRef = useRef<any>(null);
  const elementsRef = useRef<any>(null);
  const mountRef = useRef<HTMLDivElement>(null);

  const pt = language === "pt";
  const authHeaders = (): Record<string, string> => ({
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    "Content-Type": "application/json",
  });

  const loadStripeJs = (): Promise<any> =>
    new Promise((resolve, reject) => {
      const w = window as any;
      if (w.Stripe) return resolve(w.Stripe);
      const s = document.createElement("script");
      s.src = "https://js.stripe.com/v3/";
      s.onload = () => resolve((window as any).Stripe);
      s.onerror = () => reject(new Error("Failed to load Stripe.js"));
      document.head.appendChild(s);
    });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/subscriptions/setup-intent`, {
          method: "POST",
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error("setup-intent failed");
        const data = await res.json();
        if (!data.clientSecret || !data.publishableKey) throw new Error("missing keys");

        const Stripe = await loadStripeJs();
        const stripe = Stripe(data.publishableKey);
        const elements = stripe.elements({ clientSecret: data.clientSecret });
        const paymentElement = elements.create("payment");
        if (mountRef.current) paymentElement.mount(mountRef.current);

        stripeRef.current = stripe;
        elementsRef.current = elements;
        setLoading(false);
      } catch {
        setError(
          pt
            ? "Verificação de pagamento indisponível no momento. Tente novamente mais tarde."
            : "Payment verification is unavailable right now. Please try again later."
        );
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    if (!stripeRef.current || !elementsRef.current) return;
    setSubmitting(true);
    try {
      const { error: stripeError, setupIntent } = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        redirect: "if_required",
      });
      if (stripeError) throw new Error(stripeError.message);
      if (!setupIntent || setupIntent.status !== "succeeded") {
        throw new Error(pt ? "Cartão não confirmado" : "Card not confirmed");
      }

      const res = await fetch(`${API_BASE_URL}/api/subscriptions/confirm-setup`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ setupIntentId: setupIntent.id }),
      });
      if (!res.ok) throw new Error(pt ? "Falha ao validar o cartão" : "Card validation failed");

      toast({
        title: pt ? "Cartão verificado!" : "Card verified!",
        description: pt ? "Sua conta está pronta para enviar documentos." : "Your account is ready to send documents.",
      });
      setLocation("/dashboard");
    } catch (e: any) {
      toast({ title: pt ? "Erro" : "Error", description: e.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
            <CreditCard className="h-7 w-7 text-primary" />
          </div>
          <CardTitle>{pt ? "Verificação de pagamento" : "Payment verification"}</CardTitle>
          <CardDescription>
            {pt
              ? "Cadastre um cartão para liberar o envio de documentos. Nenhuma cobrança será feita agora."
              : "Add a card to enable document sending. You will not be charged now."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <p className="text-sm text-destructive text-center">{error}</p>
          ) : (
            <>
              <div ref={mountRef} className="min-h-[180px]">
                {loading && (
                  <div className="flex items-center justify-center h-[180px] text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    {pt ? "Carregando..." : "Loading..."}
                  </div>
                )}
              </div>
              <Button className="w-full" disabled={loading || submitting} onClick={handleConfirm} data-testid="button-confirm-card">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {pt ? "Salvar cartão" : "Save card"}
              </Button>
            </>
          )}
          <button
            type="button"
            className="w-full text-xs text-muted-foreground underline"
            onClick={() => setLocation("/dashboard")}
          >
            {pt ? "Deixar para depois (envio ficará bloqueado)" : "Skip for now (sending stays locked)"}
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
